import { AbsoluteFill, Freeze, Img, OffthreadVideo } from "remotion";
import { CaptionLine } from "./CaptionLine.tsx";
import { FontFaces } from "./fonts.tsx";
import { LogoMark } from "./LogoMark.tsx";
import { resolveSrc } from "./mediaSrc.ts";
import { resolveCardPx, resolveCardWidth } from "./SpeakerCard.tsx";
import {
  DEFAULT_VIDEO_DIM,
  type FontFaceSpec,
  type RenderBrand,
  type RenderLayout,
  type SerializableCue,
} from "./types.ts";
import { videoFilterStyle } from "./videoFilter.ts";

interface CoverFrameProps {
  /** Fully-resolved video src (http URL or staticFile() result) —
   *  ClipComposition resolves once and shares it with the content tree. */
  videoSrc: string;
  /** Punch caption for the cover. Rendered by the same CaptionLine as
   *  content cues, so 2-line gold-punch / em styling is identical. */
  captionText?: string;
  /** Speaker card PNG (staticFile path) at FULL opacity — no fade. */
  speakerPng?: string;
  /** Intrinsic pixel width of the card PNG (see SpeakerOverlay.pngWidthPx).
   *  Omitted → founding 1080. */
  speakerPngWidthPx?: number;
  logoSrc?: string;
  brand?: RenderBrand;
  layout?: RenderLayout;
  fontFaces?: FontFaceSpec[];
  /** Darken-layer opacity — ClipComposition passes its resolved value so
   *  cover → content never pops in brightness. */
  videoDim?: number;
}

/**
 * The feed cover (QL_COVER_HOLD): what Instagram's Reels grid shows, baked
 * into the video's first ~1s. The opening frame FROZEN under the standard
 * darken layer, the speaker name card at full opacity, the clip's punchiest
 * caption, and the brand logo — then the content plays untouched.
 *
 * Mounted only inside the cover <Sequence> in ClipComposition, so nothing
 * here exists when the flag is off.
 */
export const CoverFrame = (props: CoverFrameProps): React.JSX.Element => {
  // One synthetic cue active for the whole cover — the enclosing Sequence
  // bounds its visibility, so an effectively-infinite end keeps CaptionLine's
  // findLast lookup trivially inside the window.
  const coverCues: SerializableCue[] = props.captionText
    ? [{ startMs: 0, endMs: Number.MAX_SAFE_INTEGER, text: props.captionText }]
    : [];

  // Same placement resolution as the in-video card so a channel that moves
  // its card moves it on the cover too. Omitted → founding 130 / 320 px.
  const { leftPx: cardLeftPx, topPx: cardTopPx } = resolveCardPx(props.layout?.speakerCard);

  return (
    <AbsoluteFill style={{ backgroundColor: "#000" }}>
      {/* Fonts must register INSIDE the cover Sequence: the content tree
          (which also registers them) is unmounted during cover frames. */}
      <FontFaces faces={props.fontFaces} />
      {/* Same footage lighting as the content tree — the cover must not pop
          in exposure when the content starts. Absent/identity spreads {}, so
          the element is byte-identical to the pre-lighting cover. */}
      <Freeze frame={0}>
        <OffthreadVideo src={props.videoSrc} muted {...videoFilterStyle(props.layout?.video)} />
      </Freeze>
      {/* Same darken layer as the content tree, so cover → content doesn't
          pop in brightness. */}
      <AbsoluteFill
        style={{
          backgroundColor: `rgba(0,0,0,${props.layout?.video?.dim ?? props.videoDim ?? DEFAULT_VIDEO_DIM})`,
          pointerEvents: "none",
        }}
      />
      {props.speakerPng && (
        <AbsoluteFill style={{ pointerEvents: "none" }}>
          <div
            style={{
              position: "absolute",
              top: cardTopPx,
              left: cardLeftPx,
              width: resolveCardWidth(props.layout?.speakerCard, props.speakerPngWidthPx),
            }}
          >
            <Img
              src={resolveSrc(props.speakerPng)}
              style={{ width: "100%", height: "auto", display: "block" }}
            />
          </div>
        </AbsoluteFill>
      )}
      {coverCues.length > 0 && (
        <CaptionLine
          cues={coverCues}
          brand={props.brand}
          // The cover is a FROZEN grid frame — an entrance animation would
          // leave frame 0 captionless and karaoke would pin word 1
          // highlighted for the whole hold (critic-caught). Motion is
          // stripped; null renders identically to absent, so motion-free
          // channels stay byte-identical.
          layout={
            props.layout
              ? { ...props.layout, caption: { ...props.layout.caption, karaoke: null, animation: null } }
              : undefined
          }
        />
      )}
      <LogoMark
        src={props.logoSrc}
        widthPct={props.layout?.logo.widthPct}
        bottomPx={props.layout?.logo.bottomPx}
        opacity={props.layout?.logo.opacity}
        xPct={props.layout?.logo.xPct}
      />
    </AbsoluteFill>
  );
};
